<template>
  <Container v-if="data.length > 1 && isClient">
    <nav aria-label="Breadcrumb">
      <ul class="my-2 flex flex-wrap items-center">
        <!-- Desktop OR Mobile with ≤ 4 items -->
        <template v-if="!isMobile || data.length <= 4">
          <template v-for="(item, index) in data" :key="item.uri">
            <li class="flex items-center">
              <template v-if="index === 0">
                <NuxtLink :to="removeCmsDomain(item.uri)" class="text-black hover:underline">
                  <IconHome class="h-4 w-4" />
                </NuxtLink>
                <span class="mx-2 text-black">/</span>
              </template>
              <template v-else-if="index === data.length - 1">
                <span class="text-xs font-bold text-black lg:text-sm">
                  {{ item.title }}
                </span>
              </template>
              <template v-else>
                <NuxtLink
                  :to="removeCmsDomain(item.uri)"
                  class="text-xs text-black hover:underline lg:text-sm"
                >
                  {{ item.title }}<span class="mx-2 text-black">/</span>
                </NuxtLink>
              </template>
            </li>
          </template>
        </template>

        <!-- Mobile only: > 4 items -->
        <template v-else>
          <!-- Home -->
          <li class="flex items-center">
            <NuxtLink
              v-if="data[0]"
              :to="removeCmsDomain(data[0].uri)"
              class="text-black hover:underline"
            >
              <IconHome class="h-4 w-4" />
            </NuxtLink>
          </li>

          <!-- Collapsed Dropdown -->
          <li class="relative flex items-center">
            <span class="cursor-pointer text-xs text-black" @click="toggleDropdown">
              <span class="mx-2 text-black">/</span><span class="underline">...</span
              ><span class="mx-2 text-black">/</span>
            </span>
            <div
              v-if="isDropdownOpen"
              ref="dropdownRef"
              class="absolute top-full z-10 mt-0 rounded-md border border-blue-light bg-white px-2 py-1 text-xs shadow"
              :style="{ width: dropdownWidth + 'px' }"
            >
              <ul>
                <li
                  v-for="item in data.slice(1, data.length - 1)"
                  :key="item.uri"
                  class="whitespace-nowrap py-1"
                >
                  <NuxtLink
                    :to="removeCmsDomain(item.uri)"
                    class="text-black hover:underline"
                    @click="isDropdownOpen = false"
                  >
                    {{ item.title }}
                  </NuxtLink>
                </li>
              </ul>
            </div>
          </li>

          <!-- Current page -->
          <li v-if="last" class="flex items-center text-xs font-bold text-black">
            {{ last.title }}
          </li>
        </template>
      </ul>
    </nav>
  </Container>
</template>

<script setup lang="ts">
import { Container } from 'refresh-ui'
import { ref, watchEffect } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { removeCmsDomain } from 'refresh-common'
import IconHome from '@/components/atoms/Icons/IconHome.vue'

const isClient = typeof window !== 'undefined'

type breadcrumb = {
  title: string
  uri: string
  options: any[]
}

const props = defineProps<{ data: breadcrumb[] }>()
const data = computed(() => props.data || [])

const last = computed(() => data.value[data.value.length - 1])

const isDropdownOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)
const dropdownWidth = ref(160)

onClickOutside(dropdownRef, () => {
  isDropdownOpen.value = false
})

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value
}

const isMobile = ref(false)

onMounted(() => {
  const { width } = useWindowSize()
  watchEffect(() => {
    isMobile.value = width.value < 1024
  })
})

watchEffect(() => {
  if (isMobile.value && data.value.length > 4) {
    const collapsedItems = data.value.slice(1, data.value.length - 1)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.font = '12px sans-serif'
    const maxTextWidth = Math.max(...collapsedItems.map(item => ctx.measureText(item.title).width))
    dropdownWidth.value = maxTextWidth + 32
  }
})
</script>
